import { Button, Divider, Input, Typography } from 'antd';
import { type FC, useState } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { CommentListSkeleton } from '@/components/skeletons/CommentListSkeleton';
import { useAuth } from '@/core/providers/AuthFirebaseProvider';
import dayjs from '../../../utils/dayjs-instance';
import { useGetOrderComments, useInsertOrderComment } from '../query/orders.hooks';

const { TextArea } = Input;
const { Text } = Typography;

interface OrderCommentsProps {
  orderId: number;
}

export const OrderComments: FC<OrderCommentsProps> = ({ orderId }) => {
  const { userDataParams } = useAuth();
  const { data, isLoading } = useGetOrderComments({ orderId });
  const { mutateAsync: insertOrderComment, isPending: insertOrderCommentPending } =
    useInsertOrderComment();
  const { selectedDepartment } = useDepartments();
  const [newComment, setNewComment] = useState('');

  const handleAddComment = async () => {
    if (newComment.trim()) {
      await insertOrderComment({
        orderId,
        comment: newComment,
        authorId: `${userDataParams?.userId}`,
      });
      setNewComment('');
    }
  };

  return (
    <div>
      <Divider />
      <div style={{ display: 'flex', marginTop: '16px' }}>
        <TextArea
          rows={1}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment"
          disabled={insertOrderCommentPending}
        />
        <Button
          type="primary"
          onClick={handleAddComment}
          loading={insertOrderCommentPending}
          style={{ marginLeft: '8px' }}
        >
          Send
        </Button>
      </div>
      {isLoading ? (
        <CommentListSkeleton />
      ) : (
        <div className="ant-list comment-list">
          <div className="ant-list-header">{`${data?.order_comments.length || 0} Comments`}</div>
          <div className="ant-list-items">
            {(data?.order_comments || []).map((item) => (
              <div className="ant-list-item" key={item.id}>
                <div className="ant-list-item-meta">
                  <div className="ant-list-item-meta-content">
                    <div className="ant-list-item-meta-title">
                      <Text strong>
                        {item.user?.name || item.externalAuthor || 'Unknown'} -{' '}
                        {dayjs
                          .utc(item.createdAt)
                          .utcOffset((selectedDepartment?.timeZone || 0) * 60)
                          .format('YYYY-MM-DD HH:mm')}
                      </Text>
                    </div>
                    <div className="ant-list-item-meta-description">
                      {item.comment || 'No content'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
